import {
  DashboardBoardColumn,
  DashboardBoardLayout,
  BoardCellPlacement,
  BoardLayoutMetrics,
  DashboardModuleMeasurement,
  DashboardModuleSpan,
  DashboardPlacement,
} from '../types';

interface ComputeBoardPlacementsOptions {
  rowUnitPx?: number;
  gapPx?: number;
  defaultRowSpan?: number;
}

interface BoardInsertionSlot {
  index: number;
  order: string[];
  placement: BoardCellPlacement;
}

export const BOARD_LAYOUT_ROW_UNIT_PX = 12;
export const BOARD_LAYOUT_GAP_PX = 12;
export const BOARD_LAYOUT_DEFAULT_ROW_SPAN = 12;
export const BOARD_LAYOUT_ROOT_COLUMN_COUNT = 8;

function clampSpan(span: DashboardModuleSpan, columnCount: number) {
  return Math.max(1, Math.min(span, columnCount));
}

function canPlace(
  occupiedCells: Set<string>,
  columnStart: number,
  rowStart: number,
  columnSpan: number,
  rowSpan: number,
  columnCount: number
) {
  if (columnStart + columnSpan - 1 > columnCount) {
    return false;
  }

  for (let row = rowStart; row < rowStart + rowSpan; row += 1) {
    for (let column = columnStart; column < columnStart + columnSpan; column += 1) {
      if (occupiedCells.has(`${row}:${column}`)) {
        return false;
      }
    }
  }

  return true;
}

function markOccupied(
  occupiedCells: Set<string>,
  columnStart: number,
  rowStart: number,
  columnSpan: number,
  rowSpan: number
) {
  for (let row = rowStart; row < rowStart + rowSpan; row += 1) {
    for (let column = columnStart; column < columnStart + columnSpan; column += 1) {
      occupiedCells.add(`${row}:${column}`);
    }
  }
}

function getPlacementRowSpan(
  placementId: string,
  measurements: Record<string, DashboardModuleMeasurement>,
  defaultRowSpan: number
) {
  return measurements[placementId]?.rowSpan ?? defaultRowSpan;
}

function sortColumns(columns: DashboardBoardColumn[]) {
  return [...columns].sort((left, right) => left.order - right.order || left.columnId.localeCompare(right.columnId));
}

function sortPlacementIds(
  placementIds: string[],
  placements: Record<string, DashboardPlacement>
) {
  return [...placementIds].sort((leftId, rightId) => {
    const left = placements[leftId];
    const right = placements[rightId];
    const leftColumnOrder = left?.columnOrder ?? Number.MAX_SAFE_INTEGER;
    const rightColumnOrder = right?.columnOrder ?? Number.MAX_SAFE_INTEGER;
    const leftOrder = left?.order ?? Number.MAX_SAFE_INTEGER;
    const rightOrder = right?.order ?? Number.MAX_SAFE_INTEGER;

    return (
      leftColumnOrder - rightColumnOrder ||
      leftOrder - rightOrder ||
      leftId.localeCompare(rightId)
    );
  });
}

export function getBoardColumnRanges(columns: DashboardBoardColumn[]) {
  let nextColumnStart = 1;

  return sortColumns(columns).map((column) => {
    const columnSpan = column.span;
    const range = {
      ...column,
      columnStart: nextColumnStart,
      columnEnd: nextColumnStart + columnSpan - 1,
    };
    nextColumnStart += columnSpan;
    return range;
  });
}

export function buildVisibleOrderFromColumns(layout: DashboardBoardLayout) {
  const visiblePlacements = Object.values(layout.placements).filter((placement) => placement.visible);

  if (!layout.columns?.length) {
    return [...layout.order].filter((placementId) => Boolean(layout.placements[placementId]?.visible));
  }

  const seen = new Set<string>();
  const nextOrder: string[] = [];

  sortColumns(layout.columns).forEach((column) => {
    const columnPlacementIds = sortPlacementIds(
      visiblePlacements
        .filter((placement) => placement.columnId === column.columnId)
        .map((placement) => placement.placementId),
      layout.placements
    );

    columnPlacementIds.forEach((placementId) => {
      if (seen.has(placementId)) {
        return;
      }

      seen.add(placementId);
      nextOrder.push(placementId);
    });
  });

  sortPlacementIds(
    visiblePlacements
      .filter((placement) => !seen.has(placement.placementId))
      .map((placement) => placement.placementId),
    layout.placements
  ).forEach((placementId) => {
    if (seen.has(placementId)) {
      return;
    }

    seen.add(placementId);
    nextOrder.push(placementId);
  });

  return nextOrder;
}

export function getRowSpanFromHeight(
  heightPx: number,
  rowUnitPx = BOARD_LAYOUT_ROW_UNIT_PX,
  gapPx = BOARD_LAYOUT_GAP_PX
) {
  return Math.max(1, Math.ceil((heightPx + gapPx) / (rowUnitPx + gapPx)));
}

export function computeBoardPlacements(
  order: string[],
  placements: Record<string, DashboardPlacement>,
  measurements: Record<string, DashboardModuleMeasurement>,
  columnCount: number,
  options: ComputeBoardPlacementsOptions = {}
): BoardLayoutMetrics {
  const rowUnitPx = options.rowUnitPx ?? BOARD_LAYOUT_ROW_UNIT_PX;
  const gapPx = options.gapPx ?? BOARD_LAYOUT_GAP_PX;
  const defaultRowSpan = options.defaultRowSpan ?? BOARD_LAYOUT_DEFAULT_ROW_SPAN;
  const occupiedCells = new Set<string>();
  const computedPlacements: Record<string, BoardCellPlacement> = {};
  let maxRow = 1;

  order.forEach((placementId) => {
    const sourcePlacement = placements[placementId];
    if (!sourcePlacement) {
      return;
    }

    const columnSpan = clampSpan(sourcePlacement.span, columnCount);
    const rowSpan = getPlacementRowSpan(placementId, measurements, defaultRowSpan);
    let rowStart = 1;
    let columnStart = 1;
    let placed = false;

    while (!placed) {
      for (let column = 1; column <= columnCount; column += 1) {
        if (!canPlace(occupiedCells, column, rowStart, columnSpan, rowSpan, columnCount)) {
          continue;
        }

        columnStart = column;
        placed = true;
        break;
      }

      if (!placed) {
        rowStart += 1;
      }
    }

    markOccupied(occupiedCells, columnStart, rowStart, columnSpan, rowSpan);
    computedPlacements[placementId] = {
      placementId,
      columnStart,
      rowStart,
      columnSpan,
      rowSpan,
    };
    maxRow = Math.max(maxRow, rowStart + rowSpan - 1);
  });

  return {
    order,
    columnCount,
    rowUnitPx,
    gapPx,
    maxRow,
    placements: computedPlacements,
    sourcePlacements: placements,
    measurements,
  };
}

export function computeBoardPlacementsForFixedColumns(
  layout: DashboardBoardLayout,
  measurements: Record<string, DashboardModuleMeasurement>,
  columnCount: number,
  options: ComputeBoardPlacementsOptions = {}
): BoardLayoutMetrics {
  const visibleOrder = buildVisibleOrderFromColumns(layout);
  const rowUnitPx = options.rowUnitPx ?? BOARD_LAYOUT_ROW_UNIT_PX;
  const gapPx = options.gapPx ?? BOARD_LAYOUT_GAP_PX;

  if (!layout.columns?.length || columnCount !== BOARD_LAYOUT_ROOT_COLUMN_COUNT) {
    return computeBoardPlacements(visibleOrder, layout.placements, measurements, columnCount, options);
  }

  const computedPlacements: Record<string, BoardCellPlacement> = {};
  let maxRow = 1;
  const nextOrder: string[] = [];

  getBoardColumnRanges(layout.columns).forEach((column) => {
    const columnPlacementIds = sortPlacementIds(
      visibleOrder.filter((placementId) => layout.placements[placementId]?.columnId === column.columnId),
      layout.placements
    );

    if (columnPlacementIds.length === 0) {
      return;
    }

    const localLayout = computeBoardPlacements(
      columnPlacementIds,
      layout.placements,
      measurements,
      column.span,
      options
    );

    columnPlacementIds.forEach((placementId) => {
      const localPlacement = localLayout.placements[placementId];
      if (!localPlacement) {
        return;
      }

      computedPlacements[placementId] = {
        ...localPlacement,
        columnStart: localPlacement.columnStart + column.columnStart - 1,
      };
      maxRow = Math.max(maxRow, localPlacement.rowStart + localPlacement.rowSpan - 1);
      nextOrder.push(placementId);
    });
  });

  return {
    order: nextOrder,
    columnCount,
    rowUnitPx,
    gapPx,
    maxRow,
    placements: computedPlacements,
    sourcePlacements: layout.placements,
    measurements,
  };
}

export function computeInsertionSlots(
  layoutResult: BoardLayoutMetrics,
  activeId: string
): BoardInsertionSlot[] {
  if (!layoutResult.sourcePlacements[activeId]) {
    return [];
  }

  const baseOrder = layoutResult.order.filter((placementId) => placementId !== activeId);
  const slots: BoardInsertionSlot[] = [];

  for (let index = 0; index <= baseOrder.length; index += 1) {
    const nextOrder = [...baseOrder];
    nextOrder.splice(index, 0, activeId);

    const nextLayout = computeBoardPlacements(
      nextOrder,
      layoutResult.sourcePlacements,
      layoutResult.measurements,
      layoutResult.columnCount,
      {
        rowUnitPx: layoutResult.rowUnitPx,
        gapPx: layoutResult.gapPx,
      }
    );

    const placement = nextLayout.placements[activeId];
    if (!placement) {
      continue;
    }

    slots.push({
      index,
      order: nextOrder,
      placement,
    });
  }

  return slots;
}
